'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Mail, Lock, Eye, EyeOff, User, ArrowRight, DollarSign, TrendingUp, Users } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

export default function AffiliateRegisterPage() {
  const router = useRouter();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const generateReferralCode = (name: string): string => {
    const base = name.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 6) || 'AFF';
    const suffix = Math.random().toString(36).substring(2, 6).toUpperCase();
    return base + suffix;
  };

  const hashPassword = async (pwd: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(pwd);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!fullName || !email || !password) {
      setError('Please fill in all fields');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const supabase = createClient();
      // Check if email already exists
      const { data: existing } = await supabase
        .from('affiliates')
        .select('id')
        .eq('email', email.toLowerCase())
        .single();

      if (existing) {
        setError('An affiliate account with this email already exists');
        setLoading(false);
        return;
      }

      const passwordHash = await hashPassword(password);
      const referralCode = generateReferralCode(fullName);

      const { data, error: insertError } = await supabase
        .from('affiliates')
        .insert({
          email: email.toLowerCase(),
          full_name: fullName,
          password_hash: passwordHash,
          referral_code: referralCode,
          status: 'active',
        })
        .select('id')
        .single();

      if (insertError) throw insertError;

      // Store affiliate session
      localStorage.setItem('affiliate_id', data.id);
      localStorage.setItem('affiliate_email', email.toLowerCase());
      router.push('/affiliate/dashboard');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Registration failed. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050508] text-white flex flex-col items-center justify-center px-4">
      <div className="absolute inset-0 bg-gradient-to-br from-violet-950/30 via-[#050508] to-amber-950/10 pointer-events-none" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-violet-600/8 blur-[100px] pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="flex items-center gap-2 mb-4">
            <AppLogo size={36} />
            <span className="font-display font-bold text-xl tracking-tight">LookMax AI</span>
          </div>
          <h1 className="font-display text-2xl font-bold text-center mb-1">Join the Affiliate Program</h1>
          <p className="text-white/50 text-sm text-center">Earn 35% recurring commission every month</p>
        </div>

        {/* Earning Potential Hero Banner */}
        <div className="relative overflow-hidden bg-gradient-to-r from-violet-600/30 via-violet-700/20 to-amber-600/20 border border-violet-400/30 rounded-3xl p-5 mb-5">
          <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-violet-500/15 blur-2xl pointer-events-none" />
          <div className="relative z-10 text-center">
            <p className="text-xs font-bold text-violet-300 uppercase tracking-widest mb-2">💰 Passive Income Potential</p>
            <p className="text-4xl font-display font-black text-white leading-none mb-1">€700<span className="text-xl text-white/50">/month</span></p>
            <p className="text-sm text-white/60 mb-3">with just 100 referrals</p>
            <div className="flex flex-col gap-1.5 text-left max-w-xs mx-auto">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />
                <span className="text-xs text-white/70"><span className="font-bold text-violet-300">35% recurring</span> commission every month</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                <span className="text-xs text-white/70">100 referrals × <span className="font-bold text-amber-300">€7/mo</span> = <span className="font-bold text-emerald-300">€700/month</span></span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <span className="text-xs text-white/70">Earn <span className="font-bold text-emerald-300">passively</span> as long as they stay subscribed</span>
              </div>
            </div>
          </div>
        </div>

        {/* Commission highlights */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-3 text-center">
            <DollarSign size={18} className="text-emerald-400 mx-auto mb-1" />
            <p className="text-xs font-bold text-white">35%</p>
            <p className="text-xs text-white/40">Recurring</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-3 text-center">
            <TrendingUp size={18} className="text-violet-400 mx-auto mb-1" />
            <p className="text-xs font-bold text-white">Real-time</p>
            <p className="text-xs text-white/40">Tracking</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-3 text-center">
            <Users size={18} className="text-amber-400 mx-auto mb-1" />
            <p className="text-xs font-bold text-white">Unlimited</p>
            <p className="text-xs text-white/40">Referrals</p>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400">
              {error}
            </div>
          )}

          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-white/50 mb-2 uppercase tracking-wide">Full Name</label>
              <div className="relative">
                <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your full name"
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-violet-500/50 transition-all"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-white/50 mb-2 uppercase tracking-wide">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-violet-500/50 transition-all"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-white/50 mb-2 uppercase tracking-wide">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-12 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-violet-500/50 transition-all"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white font-bold py-3 px-4 rounded-2xl transition-all duration-200 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Create Affiliate Account
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-white/40 mt-6">
          Already have an account?{' '}
          <Link href="/affiliate/login" className="text-violet-400 hover:text-violet-300 font-semibold transition-colors">
            Sign in
          </Link>
        </p>
        <p className="text-center text-xs text-white/20 mt-3">
          <Link href="/landing-page" className="hover:text-white/40 transition-colors">
            ← Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}
